import React, { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { flushPending, countPending } from "@/lib/offlineQueue";
import { toast } from "sonner";
import { formatDateTimeJakarta } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { NAV } from "@/constants/appIds";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
} from "@/components/ui/dropdown-menu";
import {
  LayoutDashboard, Upload, ClipboardCheck, History, Settings as SettingsIcon,
  ReceiptText, LogOut, Menu, X, Sun, Moon, Bell, FileBarChart, WifiOff, UploadCloud,
} from "lucide-react";

const ITEMS = [
  { to: "/dashboard", label: "Ringkasan", icon: LayoutDashboard, id: NAV.dashboard },
  { to: "/upload", label: "Unggah Nota", icon: Upload, id: NAV.upload },
  { to: "/review", label: "Pemeriksaan Admin", icon: ClipboardCheck, id: NAV.review, roles: ["admin", "owner"] },
  { to: "/history", label: "Riwayat Belanja", icon: History, id: NAV.history },
  { to: "/reports", label: "Rekap Bulanan", icon: FileBarChart, id: NAV.reports, roles: ["owner", "admin"] },
  { to: "/settings", label: "Pengaturan", icon: SettingsIcon, id: NAV.settings, roles: ["owner", "admin"] },
];

const ROLE_LABEL = { owner: "Pemilik", admin: "Admin Pemeriksa", uploader: "Pengunggah" };

function NotificationBell({ testId = NAV.bell }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const enabled = ["admin", "owner"].includes(user?.role);
  const { data } = useQuery({
    queryKey: ["notifications"],
    queryFn: async () => (await api.get("/notifications")).data,
    refetchInterval: 15000,
    enabled,
  });
  if (!enabled) return null;
  const count = data?.count || 0;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" data-testid={testId}>
          <Bell className="w-[18px] h-[18px]" />
          {count > 0 && (
            <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center">{count}</span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 p-0">
        <div className="px-3 py-2 text-sm font-semibold border-b border-border">Nota Menunggu Pemeriksaan ({count})</div>
        <div className="max-h-80 overflow-auto nh-scroll">
          {count === 0 && <div className="px-3 py-6 text-center text-sm text-muted-foreground">Tidak ada nota menunggu.</div>}
          {data?.items?.map((n) => (
            <button key={n.id} data-testid="notification-item" onClick={() => nav(`/review/${n.id}`)}
              className="w-full text-left px-3 py-2 hover:bg-secondary transition-colors border-b border-border/60 last:border-0">
              <p className="text-sm font-medium truncate">{n.supplier || "Nota tanpa supplier"}</p>
              <p className="text-xs text-muted-foreground truncate">{n.outlet_name} · {formatDateTimeJakarta(n.uploaded_at)}</p>
            </button>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [dark, setDark] = useState(() => document.documentElement.classList.contains("dark"));
  const qc = useQueryClient();
  const [online, setOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);
  const [pending, setPending] = useState(0);

  useEffect(() => {
    const refresh = async () => setPending(await countPending());
    const flush = async () => {
      const n = await flushPending();
      if (n > 0) { toast.success(`${n} nota offline berhasil diunggah.`); qc.invalidateQueries(); }
      refresh();
    };
    refresh();
    const onOn = () => { setOnline(true); flush(); };
    const onOff = () => setOnline(false);
    window.addEventListener("online", onOn);
    window.addEventListener("offline", onOff);
    window.addEventListener("offline-queue-changed", refresh);
    const iv = setInterval(refresh, 5000);
    return () => {
      window.removeEventListener("online", onOn);
      window.removeEventListener("offline", onOff);
      window.removeEventListener("offline-queue-changed", refresh);
      clearInterval(iv);
    };
  }, [qc]);

  const offlineBadge = (!online || pending > 0) ? (
    <div data-testid="offline-badge"
      className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${online ? "bg-amber-100 text-amber-800" : "bg-red-100 text-red-700"}`}>
      {online ? <UploadCloud className="w-3.5 h-3.5" /> : <WifiOff className="w-3.5 h-3.5" />}
      <span>{!online ? (pending > 0 ? `Offline · ${pending} antre` : "Offline") : `${pending} antre unggah`}</span>
    </div>
  ) : null;

  const toggleDark = () => {
    document.documentElement.classList.toggle("dark");
    setDark((d) => !d);
  };

  const items = ITEMS.filter((i) => !i.roles || i.roles.includes(user?.role));
  const filteredBottom = items.filter((i) => i.to !== "/upload");
  const bottomLeft = filteredBottom.slice(0, 2);
  const bottomRight = filteredBottom.slice(2, 3);
  const SHORT = { "/dashboard": "Ringkasan", "/review": "Periksa", "/history": "Riwayat", "/reports": "Rekap", "/settings": "Atur" };
  const shortLabel = (to) => SHORT[to] || "";

  const NavItems = ({ onClick }) => (
    <nav className="flex flex-col gap-1">
      {items.map((it) => (
        <NavLink key={it.to} to={it.to} onClick={onClick} data-testid={it.id}
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
              isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary hover:text-foreground"
            }`
          }>
          <it.icon className="w-[18px] h-[18px]" />
          {it.label}
        </NavLink>
      ))}
    </nav>
  );

  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar desktop */}
      <aside className="hidden lg:flex flex-col w-64 border-r border-border bg-card/60 p-4 sticky top-0 h-screen">
        <div className="flex items-center gap-2 px-2 py-3 mb-4">
          <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
            <ReceiptText className="w-[18px] h-[18px] text-primary-foreground" />
          </div>
          <span className="font-mono font-bold tracking-tight">Nota Harian</span>
        </div>
        <NavItems />
        <div className="mt-auto pt-4 border-t border-border">
          <div className="px-2 py-2">
            <p className="text-sm font-medium truncate">{user?.name}</p>
            <p className="text-xs text-muted-foreground">{ROLE_LABEL[user?.role]}</p>
          </div>
          <Button variant="ghost" size="sm" onClick={logout} data-testid={NAV.themeToggle === "x" ? "" : "logout-button"}
            className="w-full justify-start text-muted-foreground">
            <LogOut className="w-4 h-4 mr-2" /> Keluar
          </Button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="lg:hidden sticky top-0 z-30 flex items-center justify-between px-4 h-14 border-b border-border bg-card">
          <div className="flex items-center gap-2">
            <ReceiptText className="w-5 h-5" />
            <span className="font-mono font-bold text-sm">Nota Harian</span>
          </div>
          <div className="flex items-center gap-1">
            {offlineBadge}
            <NotificationBell testId="notification-bell-mobile" />
            <Button variant="ghost" size="icon" onClick={toggleDark} data-testid={NAV.themeToggle}>
              {dark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
            <Button variant="ghost" size="icon" onClick={() => setOpen(true)}>
              <Menu className="w-5 h-5" />
            </Button>
          </div>
        </header>

        {/* desktop header */}
        <header className="hidden lg:flex items-center justify-end gap-2 px-6 h-14 border-b border-border bg-card/40">
          {offlineBadge}
          <NotificationBell />
          <Button variant="ghost" size="icon" onClick={toggleDark} data-testid={NAV.themeToggle}>
            {dark ? <Sun className="w-[18px] h-[18px]" /> : <Moon className="w-[18px] h-[18px]" />}
          </Button>
          <Button size="sm" onClick={() => nav("/upload")} data-testid={NAV.quickUpload} className="rounded-full">
            <Upload className="w-4 h-4 mr-2" /> Unggah Nota
          </Button>
        </header>

        <main className="flex-1 p-4 sm:p-6 lg:p-8 max-w-[1400px] w-full mx-auto nh-scroll pb-28 lg:pb-8">
          <Outlet />
        </main>

        {/* Mobile bottom navigation — thumb-friendly, upload-first */}
        <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-card/95 backdrop-blur border-t border-border flex items-stretch justify-around px-1"
          style={{ paddingBottom: "env(safe-area-inset-bottom)" }}>
          {bottomLeft.map((it) => (
            <NavLink key={it.to} to={it.to} data-testid={`bottom-${it.id}`}
              className={({ isActive }) => `flex flex-col items-center justify-center gap-0.5 py-2 flex-1 text-[10px] font-medium transition-colors ${isActive ? "text-primary" : "text-muted-foreground"}`}>
              <it.icon className="w-[22px] h-[22px]" />{shortLabel(it.to)}
            </NavLink>
          ))}
          <button onClick={() => nav("/upload")} data-testid="bottom-upload" className="flex flex-col items-center justify-end flex-1">
            <span className="w-14 h-14 -mt-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-lg ring-4 ring-background active:scale-95 transition-transform">
              <Upload className="w-6 h-6" />
            </span>
            <span className="text-[10px] mt-0.5 font-medium text-muted-foreground">Unggah</span>
          </button>
          {bottomRight.map((it) => (
            <NavLink key={it.to} to={it.to} data-testid={`bottom-${it.id}`}
              className={({ isActive }) => `flex flex-col items-center justify-center gap-0.5 py-2 flex-1 text-[10px] font-medium transition-colors ${isActive ? "text-primary" : "text-muted-foreground"}`}>
              <it.icon className="w-[22px] h-[22px]" />{shortLabel(it.to)}
            </NavLink>
          ))}
          <button onClick={() => setOpen(true)} data-testid="bottom-more"
            className="flex flex-col items-center justify-center gap-0.5 py-2 flex-1 text-[10px] font-medium text-muted-foreground">
            <Menu className="w-[22px] h-[22px]" />Lainnya
          </button>
        </nav>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-0 h-full w-72 bg-card p-4 flex flex-col nh-fade">
            <div className="flex items-center justify-between mb-4">
              <span className="font-mono font-bold">Menu</span>
              <Button variant="ghost" size="icon" onClick={() => setOpen(false)}><X className="w-5 h-5" /></Button>
            </div>
            <NavItems onClick={() => setOpen(false)} />
            <div className="mt-auto pt-4 border-t border-border">
              <div className="px-2 py-2">
                <p className="text-sm font-medium">{user?.name}</p>
                <p className="text-xs text-muted-foreground">{ROLE_LABEL[user?.role]}</p>
              </div>
              <Button variant="ghost" size="sm" onClick={logout} data-testid="logout-button-mobile"
                className="w-full justify-start text-muted-foreground">
                <LogOut className="w-4 h-4 mr-2" /> Keluar
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
